import { cva } from 'class-variance-authority';

export const CHECKBOX_STATE = {
  DEFAULT: 'default',
  DESTRUCTIVE: 'destructive',
  DISABLED: 'disabled'
} as const;

// Base checkbox control (the square box itself)
export const baseCheckboxStyle = [
  'peer',
  'inline-flex',
  'shrink-0',
  'items-center',
  'justify-center',
  'rounded-xs',
  'border',
  'cursor-pointer',
  'focus-visible:outline-none',
  'focus-visible:ring-2',
  'focus-visible:ring-cic-blue-500',
  'focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed',
  'disabled:opacity-100', 
].join(' ');

// Checkbox variants based on Figma design
export const checkboxRootVariants = cva(baseCheckboxStyle, {
  variants: {
    state: {
      // Default state (blue)
      [CHECKBOX_STATE.DEFAULT]: [
        'data-[state=unchecked]:bg-primary',
        'data-[state=unchecked]:border-primary',
        'data-[state=checked]:bg-pressed-alt',
        'data-[state=checked]:border-strong',
        'data-[state=checked]:text-invert',
        'data-[state=indeterminate]:bg-pressed-alt',
        'data-[state=indeterminate]:border-strong',
        'data-[state=indeterminate]:text-invert'
      ].join(' '),
      // Destructive state (red) - all states
      [CHECKBOX_STATE.DESTRUCTIVE]: [
        // Unchecked: white bg, error-500 border
        'data-[state=unchecked]:bg-primary',
        'data-[state=unchecked]:border-negative',
        // Checked: error-500 bg (#F04438), error-500 border, white icon
        'data-[state=checked]:bg-destructive',
        'data-[state=checked]:border-negative',
        'data-[state=checked]:text-invert',
        // Indeterminate: error-500 bg (#F04438), error-500 border, white icon
        'data-[state=indeterminate]:bg-destructive',
        'data-[state=indeterminate]:border-negative',
        'data-[state=indeterminate]:text-invert'
      ].join(' '),
      // Disabled state (gray)
      [CHECKBOX_STATE.DISABLED]: [
        // All disabled: gray-75 bg, gray-200 border, disabled text icon
        'bg-disabled',
        'border-disabled',
        'text-disabled'
      ].join(' ')
    }
  },
  defaultVariants: {
    state: CHECKBOX_STATE.DEFAULT
  }
});

// Wrapper style for checkbox with label
export const checkboxWrapperStyle = [
  'flex',
  'items-start',
  'gap-2xs',
  'group'
].join(' ');

// Label container style
export const checkboxLabelContainerStyle = [
  'flex',
  'flex-col',
  'gap-5xs'
].join(' ');

// Label with icon wrapper style
export const checkboxLabelWithIconStyle = [
  'flex',
  'items-center',
  'gap-3xs'
].join(' ');

// Primary text (label) style - default
export const checkboxLabelStyle = [
  'font-body',
  'font-medium',
  'text-sm',
  'leading-2xs',
  'text-primary',
  'cursor-pointer',
  'group-has-[:disabled]:cursor-not-allowed',
  'group-has-[:disabled]:text-disabled'
].join(' ');

// Primary text (label) style - destructive
export const checkboxLabelDestructiveStyle = [
  'font-body',
  'font-medium',
  'text-sm',
  'leading-2xs',
  'text-negative',
  'cursor-pointer',
  'group-has-[:disabled]:cursor-not-allowed',
  'group-has-[:disabled]:text-disabled'
].join(' ');

// Supporting text style - default
export const checkboxSupportingTextStyle = [
  'font-caption',
  'text-xs',
  'font-normal',
  'leading-xs',
  'text-secondary',
  'cursor-pointer',
  'group-has-[:disabled]:cursor-not-allowed',
  'group-has-[:disabled]:text-disabled'
].join(' ');

// Supporting text style - destructive
export const checkboxSupportingTextDestructiveStyle = [
  'font-caption',
  'text-xs',
  'font-normal',
  'leading-xs',
  'text-negative',
  'cursor-pointer',
  'group-has-[:disabled]:cursor-not-allowed',
  'group-has-[:disabled]:text-disabled'
].join(' ');

// Icon style - let SVG render at natural size, use block display to center properly
export const checkboxIconStyle = [
  'block', // Remove inline spacing issues
  'shrink-0' // Prevent icon from shrinking
].join(' ');
